import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const { token, user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
      setCartCount(0);
    }
  }, [user]);

  useEffect(() => {
    const count = cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    setCartCount(count);
  }, [cart]);

  const fetchCart = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(res.data);
    } catch (e) {
      console.error('Failed to fetch cart', e);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!token) return { error: 'login_required' };
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
      return { success: true };
    } catch (e) {
      return { error: e.response?.data?.detail || 'Failed to add to cart' };
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!token) return;
    try {
      await axios.put(`${API}/cart/update`, { product_id: productId, quantity }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
    } catch (e) {
      console.error('Failed to update cart', e);
    }
  };

  const removeFromCart = async (productId) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
    } catch (e) {
      console.error('Failed to remove from cart', e);
    }
  };

  const clearCart = async () => {
    if (!token) return;
    try {
      await axios.delete(`${API}/cart/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart({ items: [], total: 0 });
    } catch (e) {
      console.error('Failed to clear cart', e);
    }
  };

  return (
    <CartContext.Provider value={{ cart, cartCount, loading, fetchCart, addToCart, updateQuantity, removeFromCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
